// The Drivers list's note dialog (owner feedback 29 Oct, item 5: the call
// centre adds notes to Communication History while on the phone with a
// driver, and it took about six clicks). One click on a row's "Add note" (or
// ... > Communication history) opens it with the note box focused. The header
// names the driver with the phone number and a copy button (no spaces, for
// MicroSIP), so staff call and note in one place, and the last contact that is
// already on the driver's row (lastContactDate / Type / Agent: no extra read).
// The notes panel (DriverRemarks) reads the driver's notes only while this
// dialog is open and releases them when it closes. Closing keeps an unsaved
// note for this driver until it is saved or the person signs out.
//
//   <QuickDriverNote driver={row} open={Boolean(row)} onClose={() => setRow(null)} allowPrivate={allowPrivate} />
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { tokens as c } from '../theme';
import IconCircle from './modern/IconCircle';
import CopyButton from './modern/CopyButton';
import DriverRemarks, { noteTime, categoryLabel } from './DriverRemarks';

// The number as the Drivers list shows it: "+251912345678".
// No spaces (owner, 29 Oct 2026): "+251920439275", never the US-style "+251 92 043 9275" - in the portal every number reads as stored and pastes straight into MicroSIP (see components/modern/tableUtils.formatPhoneDisplay).
export const formatDriverPhone = (mobile) => {
  const m = /^\+251(\d{9})$/.exec(String(mobile || '').replace(/[\s-]/g, '').trim());
  return m ? `+251${m[1]}` : mobile || '';
};

/** "Last contact: Today, 3:40 pm · Booking · hana@…", or '' when the row has none. */
export const lastContactText = (driver, t) => {
  if (!driver) return '';
  const when = noteTime(driver.lastContactDate, t);
  const parts = [when, categoryLabel(driver.lastContactType, t), driver.lastContactAgent || ''].filter(Boolean);
  if (!parts.length) return '';
  return `${t('driver_note_last_contact', { defaultValue: 'Last contact' })}: ${parts.join(' · ')}`;
};

export default function QuickDriverNote({ driver, open, onClose, allowPrivate }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  // The dialog fades out after `driver` is cleared: keep showing the last one.
  const lastDriver = useRef(driver);
  if (driver) lastDriver.current = driver;
  const d = driver || lastDriver.current;

  const name = d ? `${d.firstName || ''} ${d.lastName || ''}`.trim() : '';
  const lastContact = lastContactText(d, t);
  const phoneWord = t('phone', { defaultValue: 'Phone number' });

  return (
    <Dialog
      open={Boolean(open && d)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={phone}
      aria-labelledby="quick-driver-note-title"
      // 720 px: the six category chips sit on one line (also with one chosen).
      sx={{ '& .MuiDialog-paper': { maxWidth: phone ? 'none' : 720 } }}
    >
      {/* A plain header, not DialogTitle: DialogTitle takes the dialog's
          aria-labelledby id itself, which made the whole header (buttons
          included) the dialog's name and doubled the id. The name is the h2. */}
      {d ? (
        <Box component="header" sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-start', gap: 1.5, pl: 3, pr: 8, py: 2 }}>
          <IconCircle icon={<ForumOutlinedIcon />} hue="info" size={40} sx={{ mt: 0.25 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography component="div" sx={{ fontSize: 13, fontWeight: 600, color: c.TEXT_3, lineHeight: 1.4 }}>
              {t('communication_history', { defaultValue: 'Communication history' })}
            </Typography>
            <Typography
              id="quick-driver-note-title"
              component="h2"
              sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.3, color: c.INK, overflowWrap: 'anywhere' }}
            >
              {name || t('no_name', { defaultValue: 'No name' })}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 0.5, minHeight: 32 }}>
              <Typography component="span" sx={{ fontSize: 15, color: c.INK_2, lineHeight: 1.45, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {allowPrivate ? formatDriverPhone(d.mobile) : t('hidden_demo', { defaultValue: 'Hidden for demo' })}
              </Typography>
              {allowPrivate && d.mobile ? <CopyButton value={d.mobile} phone what={phoneWord} /> : null}
            </Box>
            {lastContact ? (
              <Typography sx={{ fontSize: 13.5, color: c.TEXT_3, lineHeight: 1.45, overflowWrap: 'anywhere' }}>{lastContact}</Typography>
            ) : null}
          </Box>
          <IconButton
            aria-label={t('close_dialog', { defaultValue: 'Close' })}
            onClick={onClose}
            sx={{ position: 'absolute', right: 12, top: 12, width: 44, height: 44 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      ) : null}
      {d ? (
        <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
          <DriverRemarks key={d.id} driverId={d.id} driverData={d} autoFocus />
        </DialogContent>
      ) : null}
    </Dialog>
  );
}
